import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../api/reports';

const Dashboard = () => {
  const [period, setPeriod] = useState('day'); // 'day' or 'month'
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });

  // Build query params
  const queryParams = {
    period,
    ...(dateRange.from && { from: dateRange.from }),
    ...(dateRange.to && { to: dateRange.to })
  };

  const filterParams = {
    ...(dateRange.from && { from: dateRange.from }),
    ...(dateRange.to && { to: dateRange.to })
  };

  // Fetch dashboard data from new endpoint
  const { data: dashboardData, isLoading: isLoadingDashboard, error: dashboardError } = useQuery({
    queryKey: ['dashboard', queryParams],
    queryFn: async () => {
      return await reportsApi.getDashboard(queryParams);
    },
    retry: 1,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const { data: rechazosData, isLoading: isLoadingRechazos } = useQuery({
    queryKey: ['rechazos-por-motivo', filterParams],
    queryFn: async () => await reportsApi.getRechazosPorMotivo(filterParams),
    retry: 1,
    refetchInterval: 30000,
  });

  const { data: reasignacionesData, isLoading: isLoadingReasignaciones } = useQuery({
    queryKey: ['reasignaciones-por-motivo', filterParams],
    queryFn: async () => await reportsApi.getReasignacionesPorMotivo(filterParams),
    retry: 1,
    refetchInterval: 30000,
  });

  const { data: puntualidadData, isLoading: isLoadingPuntualidad } = useQuery({
    queryKey: ['puntualidad-origen', filterParams],
    queryFn: async () => await reportsApi.getPuntualidadOrigen(filterParams),
    retry: 1,
    refetchInterval: 30000,
  });

  const { data: duracionData, isLoading: isLoadingDuracion } = useQuery({
    queryKey: ['duracion-real-vs-estimada', filterParams],
    queryFn: async () => await reportsApi.getDuracionRealVsEstimada(filterParams),
    retry: 1,
    refetchInterval: 30000,
  });

  const { data: viajesPorAgenteData, isLoading: isLoadingViajesPorAgente } = useQuery({
    queryKey: ['viajes-por-agente', filterParams],
    queryFn: async () => await reportsApi.getViajesPorAgente(filterParams),
    retry: 1,
    refetchInterval: 30000,
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // Chart component for servicios realizados
  const ServicesChart = ({ data, period }) => {
    if (!data || data.length === 0) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>
          No hay datos para mostrar
        </div>
      );
    }

    const maxValue = Math.max(...data.map(d => d.total), 1);
    const chartHeight = 200;
    const chartWidth = Math.max(600, data.length * 60);
    const barWidth = Math.min(40, (chartWidth / data.length) - 10);
    const spacing = (chartWidth - (barWidth * data.length)) / (data.length + 1);

    return (
      <div style={{ overflowX: 'auto', padding: '20px' }}>
        <svg width={chartWidth} height={chartHeight + 60} style={{ minWidth: '100%' }}>
          {/* Bars */}
          {data.map((item, index) => {
            const barHeight = (item.total / maxValue) * chartHeight;
            const x = spacing + index * (barWidth + spacing);
            const y = chartHeight - barHeight;

            return (
              <g key={index}>
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  fill="var(--secondary)"
                  rx="4"
                  style={{ transition: 'all 0.3s' }}
                />
                <text
                  x={x + barWidth / 2}
                  y={y - 5}
                  textAnchor="middle"
                  fill="#eaeaea"
                  fontSize="12"
                  fontWeight="600"
                >
                  {item.total}
                </text>
                <text
                  x={x + barWidth / 2}
                  y={chartHeight + 20}
                  textAnchor="middle"
                  fill="#a0a0a0"
                  fontSize="11"
                  transform={`rotate(-45 ${x + barWidth / 2} ${chartHeight + 20})`}
                >
                  {formatDate(item.fecha)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    );
  };

  const MotivoBarChart = ({ data }) => {
    if (!data || data.length === 0 || data.every(d => d.total === 0)) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>
          No hay datos para mostrar
        </div>
      );
    }
    const maxValue = Math.max(...data.map(d => d.total), 1);
    return (
      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {data.map((item, index) => (
          <div key={index}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
              <span style={{ color: '#eaeaea' }}>{item.motivo}</span>
              <span style={{ color: '#eaeaea', fontWeight: '600' }}>{item.total}</span>
            </div>
            <div style={{ height: '10px', background: 'var(--panel-alt, #2a2a2a)', borderRadius: '5px', overflow: 'hidden' }}>
              <div style={{
                width: `${(item.total / maxValue) * 100}%`,
                height: '100%',
                background: 'var(--secondary)',
                borderRadius: '5px',
                transition: 'width 0.3s'
              }} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const ResumenTresBuckets = ({ items, promedioLabel, promedioValor, toleranciaMinutos, total }) => {
    if (total === 0) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>
          No hay datos para mostrar
        </div>
      );
    }
    return (
      <div style={{ padding: '20px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {items.map((item, index) => (
            <div key={index} style={{
              flex: '1',
              minWidth: '140px',
              background: 'var(--panel-alt, #2a2a2a)',
              borderRadius: '8px',
              padding: '16px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--secondary)' }}>{item.value}</div>
              <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '4px' }}>{item.label}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: '13px', opacity: 0.7, textAlign: 'center' }}>
          {promedioLabel}: <strong style={{ color: '#eaeaea' }}>{promedioValor}</strong>
          {' '}· Total: {total} · Tolerancia: ±{toleranciaMinutos} min
        </div>
      </div>
    );
  };

  // Show loading state
  if (isLoadingDashboard) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', opacity: 0.7 }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid rgba(255,255,255,0.1)',
          borderTop: '4px solid var(--secondary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }}></div>
        Cargando dashboard...
      </div>
    );
  }

  // Show error state
  if (dashboardError) {
    return (
      <div style={{
        padding: '20px',
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#ef4444', margin: '0 0 8px 0' }}>Error de Conexión</h3>
        <p style={{ margin: 0, opacity: 0.8 }}>
          No se pudo conectar con el servidor. Verifica que el backend esté funcionando.
        </p>
      </div>
    );
  }

  const dashboard = dashboardData || {};
  const serviciosRealizados = dashboard.serviciosRealizados || [];
  const choferesActivos = dashboard.choferesActivos || 0;
  const serviciosPendientes = dashboard.serviciosPendientes || 0;
  const serviciosCancelados = dashboard.serviciosCancelados || 0;
  const resumen = dashboard.resumen || {};

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600' }}>
          Dashboard
        </h1>
        <p style={{ margin: 0, opacity: 0.7, fontSize: '14px' }}>
          Resumen general de operaciones y métricas clave.
        </p>
      </div>

      {/* Filters */}
      <div className="table-card" style={{ marginBottom: '20px', padding: '16px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '150px' }}>
            <label style={{ fontSize: '13px', fontWeight: '500', opacity: 0.8 }}>Período</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              style={{
                height: '36px',
                padding: '0 12px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,.15)',
                background: 'var(--panel-alt, #2a2a2a)',
                color: '#eaeaea',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="day">Por Día</option>
              <option value="month">Por Mes</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '150px' }}>
            <label style={{ fontSize: '13px', fontWeight: '500', opacity: 0.8 }}>Desde</label>
            <input
              type="date"
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              style={{
                height: '36px',
                padding: '0 12px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,.15)',
                background: 'var(--panel-alt, #2a2a2a)',
                color: '#eaeaea',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '150px' }}>
            <label style={{ fontSize: '13px', fontWeight: '500', opacity: 0.8 }}>Hasta</label>
            <input
              type="date"
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              style={{
                height: '36px',
                padding: '0 12px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,.15)',
                background: 'var(--panel-alt, #2a2a2a)',
                color: '#eaeaea',
                fontSize: '14px'
              }}
            />
          </div>

          <button
            className="btn btn--ghost"
            onClick={() => {
              setPeriod('day');
              setDateRange({ from: '', to: '' });
            }}
            style={{ height: '36px', padding: '0 16px', fontSize: '14px' }}
          >
            Limpiar
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '20px' }}>
        {/* Choferes Activos */}
        <div className="table-card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <span className="material-symbols-outlined" style={{ color: '#3b82f6', fontSize: '24px' }}>person</span>
            <div style={{ opacity: 0.8, fontSize: '14px', fontWeight: '500' }}>Choferes Activos</div>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px' }}>{choferesActivos}</div>
          <div style={{ fontSize: '12px', opacity: 0.7 }}>
            Conductores disponibles
          </div>
        </div>

        {/* Servicios Pendientes */}
        <div className="table-card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <span className="material-symbols-outlined" style={{ color: '#f59e0b', fontSize: '24px' }}>schedule</span>
            <div style={{ opacity: 0.8, fontSize: '14px', fontWeight: '500' }}>Servicios Pendientes</div>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px' }}>{serviciosPendientes}</div>
          <div style={{ fontSize: '12px', opacity: 0.7 }}>
            Requieren atención
          </div>
        </div>

        {/* Servicios Cancelados */}
        <div className="table-card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <span className="material-symbols-outlined" style={{ color: '#ef4444', fontSize: '24px' }}>cancel</span>
            <div style={{ opacity: 0.8, fontSize: '14px', fontWeight: '500' }}>Servicios Cancelados</div>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px' }}>{serviciosCancelados}</div>
          <div style={{ fontSize: '12px', opacity: 0.7 }}>
            Cancelados en el período
          </div>
        </div>

        {/* Resumen - Total Servicios */}
        {resumen.totalServicios !== undefined && (
          <div className="table-card" style={{ padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span className="material-symbols-outlined" style={{ color: 'var(--secondary)', fontSize: '24px' }}>assessment</span>
              <div style={{ opacity: 0.8, fontSize: '14px', fontWeight: '500' }}>Total Servicios</div>
            </div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px' }}>{resumen.totalServicios || 0}</div>
            <div style={{ fontSize: '12px', opacity: 0.7 }}>
              {resumen.serviciosRealizados || 0} realizados
            </div>
          </div>
        )}
      </div>

      {/* Services Chart */}
      <div className="table-card" style={{ marginBottom: '20px' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,.1)' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            Servicios Realizados {period === 'day' ? 'por Día' : 'por Mes'}
          </h3>
        </div>
        <ServicesChart data={serviciosRealizados} period={period} />
      </div>

      <div className="table-card" style={{ marginBottom: '20px' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,.1)' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Rechazos por Motivo</h3>
        </div>
        {isLoadingRechazos ? (
          <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>Cargando...</div>
        ) : (
          <MotivoBarChart data={rechazosData?.porMotivo || []} />
        )}
      </div>

      <div className="table-card" style={{ marginBottom: '20px' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,.1)' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Reasignaciones por Motivo</h3>
          {reasignacionesData && reasignacionesData.totalReasignaciones > 0 && (
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', opacity: 0.7 }}>
              {reasignacionesData.totalReasignaciones} reasignación(es) en {reasignacionesData.viajesUnicosReasignados} viaje(s) distinto(s)
            </p>
          )}
        </div>
        {isLoadingReasignaciones ? (
          <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>Cargando...</div>
        ) : (
          <MotivoBarChart data={reasignacionesData?.porMotivo || []} />
        )}
      </div>

      <div className="table-card" style={{ marginBottom: '20px' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,.1)' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Puntualidad de Llegada a Origen</h3>
        </div>
        {isLoadingPuntualidad ? (
          <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>Cargando...</div>
        ) : (
          <ResumenTresBuckets
            items={[
              { label: 'A tiempo', value: puntualidadData?.resumen?.aTiempo || 0 },
              { label: 'Adelantado', value: puntualidadData?.resumen?.adelantado || 0 },
              { label: 'Tarde', value: puntualidadData?.resumen?.tarde || 0 }
            ]}
            promedioLabel="Promedio"
            promedioValor={`${puntualidadData?.promedioMinutos ?? 0} min`}
            toleranciaMinutos={puntualidadData?.toleranciaMinutos ?? 5}
            total={puntualidadData?.total || 0}
          />
        )}
      </div>

      <div className="table-card" style={{ marginBottom: '20px' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,.1)' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Duración Real vs. Estimada (Google Maps)</h3>
        </div>
        {isLoadingDuracion ? (
          <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>Cargando...</div>
        ) : (
          <ResumenTresBuckets
            items={[
              { label: 'Más largo de lo estimado', value: duracionData?.resumen?.masLargoQueEstimado || 0 },
              { label: 'Similar a lo estimado', value: duracionData?.resumen?.similarAEstimado || 0 },
              { label: 'Más corto de lo estimado', value: duracionData?.resumen?.masCortoQueEstimado || 0 }
            ]}
            promedioLabel="Diferencia promedio"
            promedioValor={`${duracionData?.promedioDiferenciaMinutos ?? 0} min`}
            toleranciaMinutos={duracionData?.toleranciaMinutos ?? 5}
            total={duracionData?.total || 0}
          />
        )}
      </div>

      <div className="table-card" style={{ marginBottom: '20px' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,.1)' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Viajes Tocados por Agente en cada Jornada</h3>
        </div>
        {isLoadingViajesPorAgente ? (
          <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>Cargando...</div>
        ) : !viajesPorAgenteData?.agentes?.length ? (
          <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>
            No hay datos para mostrar
          </div>
        ) : (
          <div style={{ overflowX: 'auto', padding: '16px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: `${400 + viajesPorAgenteData.agentes.length * 140}px` }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,.05)' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#cfcfcf' }}>Fecha</th>
                  {viajesPorAgenteData.agentes.map((agente) => (
                    <th key={agente.id} style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', fontSize: '13px', color: '#cfcfcf' }}>
                      {agente.nombre}
                    </th>
                  ))}
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', fontSize: '13px', color: '#cfcfcf' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {viajesPorAgenteData.dias.map((diaRow) => (
                  <tr key={diaRow.dia} style={{ borderTop: '1px solid rgba(255,255,255,.08)', color: '#e5e5e5' }}>
                    <td style={{ padding: '10px 16px', fontSize: '14px' }}>{formatDate(diaRow.dia)}</td>
                    {viajesPorAgenteData.agentes.map((agente) => (
                      <td key={agente.id} style={{ padding: '10px 16px', fontSize: '14px', textAlign: 'center' }}>
                        {diaRow.porAgente[agente.id] ?? '-'}
                      </td>
                    ))}
                    <td style={{ padding: '10px 16px', fontSize: '14px', textAlign: 'center', fontWeight: '600' }}>
                      {diaRow.total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const getBadgeClass = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('pendiente') || s.includes('iniciar')) return 'emerald';
  if (s.includes('camino') || s.includes('salida')) return 'yellow';
  if (s.includes('espera')) return 'yellow';
  if (s.includes('solicitado')) return 'yellow';
  if (s.includes('ruta')) return 'orange';
  if (s.includes('destino')) return 'orange';
  if (s.includes('finalizado') || s.includes('final')) return 'green';
  if (s.includes('cancel')) return 'red';
  if (s.includes('rechazado')) return 'red';
  return 'gray';
};

const formatStatusLabel = (status) => {
  const statusMap = {
    'PENDIENTE_INICIAR': 'Pendiente Iniciar',
    'EN_CAMINO_SALIDA': 'En Camino a Salida',
    'ESPERA_SALIDA': 'Espera Salida',
    'SOLICITADO': 'Solicitado',
    'EN_RUTA': 'En Ruta',
    'EN_DESTINO': 'En Destino',
    'FINALIZADO': 'Finalizado',
    'CANCELADO': 'Cancelado',
    'RECHAZADO': 'Rechazado'
  };
  return statusMap[status] || status;
};

export default Dashboard;
